import type { OrganizationWithChildren, TenantWithChildren, TenantWithOrganization } from "@scayla/api-client-fetch";
import type { IconName } from "@scayla/components";

import { $t } from "@/i18n";

/**
 * Checks if the given organization is the root organization of the tenant.
 * Criteria for a root organization:
 * 1. The organization's `tenantId` matches the tenant's ID.
 * 2. The organization has no parent (`parentId` is null).
 * @param tenant - The current tenant context.
 * @param org - The organization object to evaluate.
 * @returns {boolean} true if the organization is the top-level root for the given tenant.
 */
export const isRootOrg = (tenant?: TenantWithOrganization, org?: OrganizationWithChildren | null): boolean => {
  if (!org || !tenant) return false;
  return tenant.id === org.tenantId && org.parentId == null;
};

/**
 * Returns a localized label based on the entity's role in the hierarchy.
 * Selection logic:
 * - If 'isTenant' is true -> "Tenant"
 * - If it matches the tenant's root -> "Root Organization"
 * - If it has children -> "OU Group" (Organizational Unit Group)
 * - Otherwise -> "Organization"
 * @param isTenant - Flag indicating the entity should be treated as a Tenant.
 * @param tenant - Tenant context used to verify if the organization is a root.
 * @param org - The organization object to determine the label for.
 * @returns {string} The localized display name for the entity type.
 */
export const getLabel = (
  isTenant: boolean,
  tenant?: TenantWithOrganization,
  org?: OrganizationWithChildren | null,
): string => {
  if (isTenant) return $t("common.label.mandator");

  if (tenant && org && isRootOrg(tenant, org)) {
    return $t("common.label.rootOrganization");
  }

  if (org?.children && org.children.length > 0) {
    return $t("common.label.ouGroup");
  }

  return $t("common.label.organization");
};

/**
 * Returns a Phosphor icon identifier based on the entity's hierarchical role.
 * Icon mapping:
 * - Tenant -> phosphor/city
 * - Root Org -> phosphor/building-apartment
 * - OU Group -> phosphor/network
 * - Org -> phosphor/building
 * @param isTenant - Flag indicating the entity should be treated as a Tenant.
 * @param tenant - Tenant context used to verify if the organization is a root.
 * @param org - The organization object to determine the icon for.
 * @returns {string} The string path/name of the icon.
 */
export const getIcon = (
  isTenant: boolean,
  tenant?: TenantWithOrganization,
  org?: OrganizationWithChildren | null,
): IconName => {
  if (isTenant) {
    return "phosphor/city";
  }

  if (tenant && org && isRootOrg(tenant, org)) {
    return "phosphor/building-apartment";
  }

  if (org?.children && org.children.length > 0) {
    return "phosphor/network";
  }

  return "phosphor/building";
};

/**
 * Returns the org tree children for a tenant.
 * Falls back to a single-element array with the tenant's root org if the tree has no loaded children.
 */
export const getOrgTreeForTenant = (
  tenant: TenantWithOrganization | null | undefined,
  tenantOrgTrees: Map<string, TenantWithChildren>,
): OrganizationWithChildren[] => {
  if (!tenant) return [];
  const children = tenantOrgTrees.get(tenant.id)?.children ?? [];
  if (children.length > 0) return children;
  if (tenant.organization?.name?.trim()) {
    return [{ ...tenant.organization, children: [] }];
  }
  return [];
};

/**
 * Returns the display title for the current tenant/org context.
 * Mirrors the title logic used in TenantOrgaSelect:
 * - Tenant selected + single root org with children → root org name
 * - Tenant selected → tenant name
 * - Org selected → org name
 * - Fallback to tenant's root org name if orgId matches
 */
export const getDisplayTitle = (
  isTenantSelected: boolean,
  orgTree: OrganizationWithChildren[],
  tenant?: TenantWithOrganization | null,
  org?: OrganizationWithChildren | null,
  orgId?: string | null,
): string => {
  if (!tenant) return "";

  const hasSingleRootOrg = orgTree.length === 1;

  if (isTenantSelected && hasSingleRootOrg && (orgTree[0]?.children?.length ?? 0) > 0) {
    return tenant.organization?.name ?? tenant.name;
  }

  if (isTenantSelected) return tenant.name ?? "";

  if (org?.name) return org.name;

  if (tenant.organization?.name) {
    const orgIdMatches = !orgId || tenant.organization.id === orgId;
    if (orgIdMatches) return tenant.organization.name;
  }

  return "";
};
